<template>
  <header class="site-header">
    <div class="header-container">
      <div class="logo">
        <a href="/">Games<span>Nest</span></a>
      </div>
      <nav class="main-nav">
        <a href="/games/">Latest Games</a>
        <a href="/news/">News</a>
        <a href="/reviews/">Reviews</a>
        <a href="/guides/">Guides</a>
        <a href="/deals/">Deals</a>
        <a href="/tags/">Tags</a>
        <div class="search-wrapper" v-if="isClient" ref="searchWrapper">
          <button
            class="search-toggle"
            :class="{ active: searchOpen }"
            @click="toggleSearch"
            aria-label="Search"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </button>
          <div class="search-input-area" :class="{ expanded: searchOpen }">
            <input
              ref="searchInput"
              v-model="query"
              type="text"
              placeholder="Search articles..."
              @keydown.esc="closeSearch"
              @input="onInput"
            />
          </div>
          <transition name="dropdown-fade">
            <ul
              v-if="searchOpen && results.length > 0"
              class="search-results"
            >
              <li v-for="item in results" :key="item.slug">
                <a :href="`/${item.category}/${item.slug}/`" @click="closeSearch">
                  <span class="result-category">{{ item.category }}</span>
                  <span class="result-title">{{ item.title }}</span>
                </a>
              </li>
            </ul>
          </transition>
        </div>
      </nav>
    </div>
  </header>
</template>

<script>
export default {
  name: 'SiteHeader',

  data() {
    return {
      isClient: false,
      searchOpen: false,
      query: '',
      results: [],
      debounceTimer: null,
    };
  },

  mounted() {
    this.isClient = true;
    document.addEventListener('click', this.onClickOutside);
    document.addEventListener('keydown', this.onGlobalEsc);
  },

  beforeDestroy() {
    document.removeEventListener('click', this.onClickOutside);
    document.removeEventListener('keydown', this.onGlobalEsc);
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
  },

  methods: {
    toggleSearch() {
      this.searchOpen = !this.searchOpen;
      if (this.searchOpen) {
        this.$nextTick(() => {
          if (this.$refs.searchInput) this.$refs.searchInput.focus();
        });
      } else {
        this.resetSearch();
      }
    },

    closeSearch() {
      this.searchOpen = false;
      this.resetSearch();
    },

    resetSearch() {
      this.query = '';
      this.results = [];
    },

    onInput() {
      if (this.debounceTimer) clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.performSearch();
      }, 300);
    },

    performSearch() {
      const q = this.query.trim().toLowerCase();
      if (!q) {
        this.results = [];
        return;
      }

      const articles = (this.$store.state.articles && this.$store.state.articles.articles) || [];
      const matched = [];

      for (let i = 0; i < articles.length && matched.length < 5; i++) {
        const a = articles[i];
        const title = (a.title || '').toLowerCase();
        const excerpt = (a.excerpt || '').toLowerCase();
        const tags = Array.isArray(a.tags)
          ? a.tags.join(' ').toLowerCase()
          : '';

        if (title.includes(q) || excerpt.includes(q) || tags.includes(q)) {
          matched.push(a);
        }
      }

      this.results = matched;
    },

    onClickOutside(e) {
      if (!this.searchOpen) return;
      const wrapper = this.$refs.searchWrapper;
      if (wrapper && !wrapper.contains(e.target)) {
        this.closeSearch();
      }
    },

    onGlobalEsc(e) {
      if (e.key === 'Escape' && this.searchOpen) {
        this.closeSearch();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@use '@/css/variables.scss' as *;

.site-header {
  background-color: rgba($bg-primary, 0.8);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  padding: rem(20) 0;
  position: sticky;
  top: 0;
  z-index: 100;
}

.header-container {
  max-width: $container-max-width;
  margin: 0 auto;
  padding: 0 rem(24);
  display: flex;
  flex-direction: column;
  gap: rem(16);
  align-items: center;

  @include pc {
    flex-direction: row;
    justify-content: space-between;
    gap: 0;
  }
}

.logo a {
  font-size: rem(28);
  font-weight: 800;
  color: $text-primary;
  text-decoration: none;
  font-family: $font-family-heading;
  letter-spacing: -0.5px;
  transition: opacity $transition-speed;

  span {
    background: linear-gradient(90deg, $primary-color, $secondary-color);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  &:hover {
    opacity: 0.8;
  }
}

.main-nav {
  display: flex;
  gap: rem(16);
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;

  @include pc {
    gap: rem(32);
    flex-wrap: nowrap;
  }

  > a {
    color: $text-secondary;
    text-decoration: none;
    font-weight: 500;
    font-size: rem(15);
    transition: all $transition-speed;

    &:hover {
      color: $text-primary;
      text-shadow: 0 0 10px rgba(255, 255, 255, 0.3);
    }
  }
}

// Search
.search-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  margin-left: rem(4);

  @include pc {
    margin-left: rem(8);
  }
}

.search-toggle {
  background: none;
  border: none;
  color: $text-secondary;
  cursor: pointer;
  padding: rem(6);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all $transition-speed;
  flex-shrink: 0;

  &:hover,
  &.active {
    color: $text-primary;
    background: rgba(255, 255, 255, 0.08);
  }
}

.search-input-area {
  width: 0;
  overflow: hidden;
  opacity: 0;
  transition: width 0.35s cubic-bezier(0.4, 0, 0.2, 1),
              opacity 0.25s ease;

  &.expanded {
    width: rem(200);
    opacity: 1;

    @include pc {
      width: rem(240);
    }
  }

  input {
    width: 100%;
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: rem(8);
    padding: rem(6) rem(12);
    color: $text-primary;
    font-size: rem(14);
    outline: none;
    transition: border-color 0.2s ease;

    &::placeholder {
      color: $text-muted;
    }

    &:focus {
      border-color: rgba($primary-color, 0.5);
    }
  }
}

.search-results {
  position: absolute;
  top: calc(100% + #{rem(8)});
  right: 0;
  width: rem(320);
  max-height: rem(360);
  overflow-y: auto;
  list-style: none;
  margin: 0;
  padding: rem(6) 0;
  background: rgba($bg-secondary, 0.97);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: rem(12);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
  z-index: 200;

  li a {
    display: flex;
    align-items: center;
    gap: rem(10);
    padding: rem(10) rem(14);
    text-decoration: none;
    transition: background 0.15s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.06);
    }
  }
}

.result-category {
  flex-shrink: 0;
  font-size: rem(11);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: rem(2) rem(8);
  border-radius: rem(4);
  background: rgba($primary-color, 0.15);
  color: $primary-color;
}

.result-title {
  color: $text-primary;
  font-size: rem(14);
  font-weight: 500;
  @include text-ellipsis(1);
}

// Dropdown transition
.dropdown-fade-enter-active,
.dropdown-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.dropdown-fade-enter,
.dropdown-fade-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}
</style>
